/**
 * The alert board (PDW-184 / FE PDW-246, BE PDW-247): the KPI strip and one row per alert definition the viewer may
 * see. The shapes follow core's `AlertBoardDtos.kt`, answered unchanged by the product's `GET /api/alerts/board`.
 */

/** Whose alert a definition is: a shared one, owned by `GLOBAL`, or the viewer's own. */
export type AlertCategory = 'GLOBAL' | 'PRIVATE';

/** A definition's alert state on the board; a retired one is listed only with Include retired alerts. */
export type AlertBoardAlertState = 'ACTIVE' | 'RETIRED';

/**
 * The strip over every active alert the filters list, never a retired one. A rate or an average
 * with nothing to compute is null and shows an em dash. The rate is a percentage, unrounded.
 */
export type AlertBoardKpiDto = {
  definedAlerts: number;
  activeEpisodes: number;
  snoozedEpisodes: number;
  solvedRate: number | null;
  avgDaysToSolve: number | null;
};

/**
 * One alert with the counts over the episodes the viewer may see. `data` is the current criteria as stored, for the
 * definition tooltip. `solvedRate` is a percentage, unrounded; `avgDaysToSolve` is rounded to one decimal by core.
 */
export type AlertBoardRowDto = {
  userFilterId: number;
  name: string;
  isGlobal: boolean;
  /** The saved-filter component the alert is defined on, shown as its origin. */
  component: string;
  alertState: AlertBoardAlertState;
  weight: number;
  data: string;
  activePatients: number;
  activeEpisodes: number;
  solvedRate: number | null;
  avgDaysToSolve: number | null;
};

/** Core-entities' `RestPage` as it is serialized, the alert list's page. */
export type AlertBoardRestPage<T> = {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages?: number;
};

/** Every alert the filters list in one response, never paged: the table scrolls them (PDW-413). */
export type AlertBoardDto = {
  kpis: AlertBoardKpiDto;
  definitions: AlertBoardRowDto[];
};

/**
 * The properties the board may be sorted by on request (core's `AlertBoardSort`, the state excluded). Every key the
 * request leaves out follows in the default order: current before retired, weight, active episodes, active patients,
 * solved rate, avg time to solve, all descending.
 */
export type AlertBoardSortProperty = 'weight' | 'activePatients' | 'activeEpisodes' | 'solvedRate' | 'avgDaysToSolve';
export const ALERT_BOARD_SORT_PROPERTIES: AlertBoardSortProperty[] = [
  'weight',
  'activePatients',
  'activeEpisodes',
  'solvedRate',
  'avgDaysToSolve',
];
/** The column the board's default order is shown on: with retired alerts left out it orders by weight descending. */
export const ALERT_BOARD_DEFAULT_SORT_PROPERTY: AlertBoardSortProperty = 'weight';
